import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import type { StartNodeTrigger } from '../start.types'
import { NodeDefinition } from '../../../types'
import { StartEditorNode } from '../start.editorNode'
import { StartNodeIcon } from '../start.icon'
import { StartQuoteRequiresApprovalConfigForm } from './startQuoteRequiresApproval.configForm'
import {
  createForkJoinTask,
  createJoinTask,
  createSimpleTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import {
  handlesConnected,
  requiresPublishQuote,
  triggerTypeRequired
} from '../start.validators'
import { QuoteRequiresApprovalExecutionSummary } from './QuoteRequiresApproval.executionSummary'
// Imported directly from the function-strings module (rather than the
// `conditions` barrel) to avoid a circular import: the barrel re-exports
// `getUpstreamConditionalProperties`, which transitively pulls the node
// definitions registry — including this file — back into the cycle. By
// importing the factories directly we sidestep that path so the operator
// declarations below can call them at module init time.
import {
  arrayExcludesAll,
  arrayIncludesAll,
  durationEquals,
  durationGreaterThan,
  durationLessThan,
  durationNotEquals,
  equalsAmount,
  equalsText,
  notEqualsText,
  lessThanAmount,
  moreThanAmount,
  paymentTermsEquals,
  paymentTermsIsNotSet,
  paymentTermsLessThan,
  paymentTermsMoreThan,
  paymentTermsNotEquals,
  textExcludes,
  textIncludes,
  textIsEmpty,
  textIsNotEmpty,
  arrayExcludes,
  arrayIncludes
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  amountWithUnitInput,
  CurrencyValueInput,
  DealTypeValueInput,
  DurationValueInput,
  formatAmountWithUnit,
  formatConditionDuration,
  formatCurrencyCode,
  formatDealType,
  formatPaymentTerms,
  formatProductCount,
  formatText,
  formatUserCount,
  PaymentTermsValueInput,
  ProductValueInput,
  TextValueInput,
  UserValueInput
} from '../../switch/components/GroupEditor/inputs'
import { productConditionalProperty } from './conditionalProperties/product/product.conditionalProperty'
import { phaseConditionalProperty } from './conditionalProperties/phase/phase.conditionalProperty'
import { quoteTemplateConditionalProperty } from './conditionalProperties/quoteTemplate/quoteTemplate.conditionalProperty'
import { previousApprovalConditionalProperty } from './conditionalProperties/previousApproval/previousApproval.conditionalProperty'
import { customerConditionalProperty } from '../conditionalProperties/customer/customer.conditionalProperty'
import { fetchCustomerCustomPropertyKeys } from '../conditionalProperties/customer/fetchCustomerCustomPropertyKeys'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export type Config = {
  name: string
  trigger: StartNodeTrigger
}

// Bound at module scope so the component identity stays stable across renders.
const contractValueInput = amountWithUnitInput({ allowCurrency: true })
const discountValueInput = amountWithUnitInput({
  allowCurrency: true,
  allowPercentage: true
})

// Three-column layout for amount-with-unit operators: the operator dropdown
// flexes to fill, then a fixed-width unit selector and value input. The
// `valueInputComponent` factory renders its children with `gridArea: 'unit'`
// and `gridArea: 'value'`.
const amountWithUnitGridStyle = () => ({
  gridTemplateAreas: '"operator unit value"',
  gridTemplateColumns: '1fr 80px 104px',
  gap: '8px'
})

// Four-column layout for duration operators: the operator dropdown flexes to
// fill, then three fixed-width number inputs for years/months/days. The
// `DurationValueInput` component renders its children with the matching
// `gridArea` props.
const durationGridStyle = () => ({
  gridTemplateAreas: '"operator years months days"',
  gridTemplateColumns: '1fr 48px 48px 48px',
  gap: '8px'
})

type Outputs =
  | 'quote'
  | 'customer'
  | 'customerId'
  | 'customProperties'
  | 'user'
  | 'requestorComment'
  | 'signatureConfiguration'
  | 'totalContractValue'
  | 'totalDiscountValue'
  | 'totalDuration'
  | 'rawText'
  | 'productIds'
  | 'productDiscounts'
  | 'phases'
  | 'currency'
  | 'startDate'
  | 'dealType'
  | 'paymentTerms'
  | 'requestorId'
  | 'attachments'

type TaskName =
  | 'startFork'
  | 'getQuote'
  | 'getCustomer'
  | 'getUser'
  | 'startJoin'

const taskReferenceBuilder = getTaskReferenceName<TaskName>(
  'START_QUOTE_REQUIRES_APPROVAL'
)

// Async registry entry: the CUSTOMER conditional property generates one
// operator per customer custom-property key, which must be fetched first. The
// whole definition is produced once at load (see `resolveNodeDefinitions`).
export const startQuoteRequiresApprovalNodeDefinition: () => Promise<
  NodeDefinition<Config, string, Outputs>
> = async () => {
  const customPropertyKeys = await fetchCustomerCustomPropertyKeys()

  return {
    name: 'Start',
    startNodeTriggerType: 'QUOTE_REQUIRES_APPROVAL',
    description: 'Quote approval',
    editorNode: StartEditorNode,
    icon: StartNodeIcon,
    configForm: StartQuoteRequiresApprovalConfigForm,
    defaultConfig: {
      name: '',
      trigger: 'QUOTE_REQUIRES_APPROVAL'
    },
    validation: {
      node: { ERROR: [requiresPublishQuote], WARN: [] },
      config: {
        name: { ERROR: [], WARN: [] },
        trigger: { ERROR: [triggerTypeRequired], WARN: [] }
      },
      handles: { ERROR: [handlesConnected], WARN: [] }
    },
    isPersistent: true,
    outputMapping: nodeId => {
      const taskReferenceName = taskReferenceBuilder(nodeId)

      return {
        quote: {
          id: `${taskReferenceName('getQuote')}.output.quote`,
          semanticType: semanticTypes.sequence.quote.quote
        },
        customer: {
          id: `${taskReferenceName('getCustomer')}.output.customer`,
          semanticType: semanticTypes.sequence.customer.customer
        },
        // The customer id and custom-properties record back the CUSTOMER
        // conditional property (identity Includes/Excludes and Custom property
        // comparisons respectively).
        customerId: {
          id: `${taskReferenceName('getCustomer')}.output.customer.id`,
          semanticType: semanticTypes.sequence.customer.id
        },
        customProperties: {
          id: `${taskReferenceName('getCustomer')}.output.customer.customProperties`,
          semanticType: semanticTypes.sequence.customer.customProperties
        },
        user: {
          id: `${taskReferenceName('getUser')}.output.user`,
          semanticType: semanticTypes.sequence.user
        },
        requestorComment: {
          id: 'workflow.input.comment',
          semanticType: semanticTypes.sequence.quoteApproval.requestorComment
        },
        signatureConfiguration: {
          id: 'workflow.input.signatureConfiguration',
          semanticType:
            semanticTypes.sequence.quoteApproval.signatureConfiguration
        },
        totalContractValue: {
          id: `${taskReferenceName('getQuote')}.output.quote.totalContractValue`,
          semanticType: semanticTypes.sequence.quote.totalContractValue
        },
        totalDiscountValue: {
          id: `${taskReferenceName('getQuote')}.output.quote.totalDiscountValue`,
          semanticType: semanticTypes.sequence.quote.totalDiscountValue
        },
        totalDuration: {
          id: `${taskReferenceName('getQuote')}.output.quote.totalDuration`,
          semanticType: semanticTypes.sequence.quote.totalDuration
        },
        rawText: {
          id: `${taskReferenceName('getQuote')}.output.quote.rawText`,
          semanticType: semanticTypes.sequence.quote.rawText
        },
        productIds: {
          id: `${taskReferenceName('getQuote')}.output.quote.products`,
          semanticType: semanticTypes.sequence.quote.productIds
        },
        productDiscounts: {
          id: `${taskReferenceName('getQuote')}.output.quote.productDiscounts`,
          semanticType: semanticTypes.sequence.quote.productDiscounts
        },
        phases: {
          id: `${taskReferenceName('getQuote')}.output.quote.phases`,
          semanticType: semanticTypes.sequence.quote.phases
        },
        currency: {
          id: `${taskReferenceName('getQuote')}.output.quote.currency`,
          semanticType: semanticTypes.sequence.quote.currency
        },
        startDate: {
          id: `${taskReferenceName('getQuote')}.output.quote.preliminaryBillingStartDate`,
          semanticType: semanticTypes.sequence.quote.startDate
        },
        dealType: {
          id: `${taskReferenceName('getQuote')}.output.quote.dealType`,
          semanticType: semanticTypes.sequence.quote.dealType
        },
        // A quote that leaves its payment terms unset reports null here and
        // inherits the account default, which the workflow never sees — so
        // PAYMENT_TERMS answers only for terms a quote states itself.
        paymentTerms: {
          id: `${taskReferenceName('getQuote')}.output.quote.defaultDueDateDays`,
          semanticType: semanticTypes.sequence.quote.paymentTerms
        },
        requestorId: {
          id: 'workflow.input.requestorId',
          semanticType: semanticTypes.sequence.requestorId
        },
        // Backs the QUOTE_TEMPLATE.ATTACHMENTS comparison — the quote side of
        // the equality check against a template's attachments.
        attachments: {
          id: `${taskReferenceName('getQuote')}.output.quote.attachmentAssets`,
          semanticType: semanticTypes.sequence.quote.attachments
        }
      }
    },
    taskTransformer: ({ nodeId }) => {
      const taskReferenceName = taskReferenceBuilder(nodeId)

      return [
        createForkJoinTask({
          name: 'start-fork',
          taskReferenceName: taskReferenceName('startFork'),
          forkTasks: [
            [
              createSimpleTask({
                name: 'get_sequence_data',
                taskReferenceName: taskReferenceName('getQuote'),
                inputParameters: {
                  taskExecutorConfiguration: {
                    executor: TASK_EXECUTORS.quote,
                    version: 1
                  },
                  data: wrapVariables({
                    quoteId: 'workflow.input.quoteId'
                  })
                }
              }),
              createSimpleTask({
                name: 'get_sequence_data',
                taskReferenceName: taskReferenceName('getCustomer'),
                inputParameters: {
                  taskExecutorConfiguration: {
                    executor: TASK_EXECUTORS.customer,
                    version: 1
                  },
                  data: wrapVariables({
                    customerId: `${taskReferenceName('getQuote')}.output.quote.customerId`
                  })
                }
              })
            ],
            [
              createSimpleTask({
                name: 'get_sequence_data',
                taskReferenceName: taskReferenceName('getUser'),
                inputParameters: {
                  taskExecutorConfiguration: {
                    executor: TASK_EXECUTORS.user,
                    version: 1
                  },
                  data: wrapVariables({
                    userId: 'workflow.input.requestorId'
                  })
                }
              })
            ]
          ],
          inputParameters: {}
        }),
        createJoinTask({
          name: 'start-join',
          taskReferenceName: taskReferenceName('startJoin'),
          joinOn: [
            taskReferenceName('getCustomer'),
            taskReferenceName('getUser')
          ],
          inputParameters: {}
        })
      ]
    },
    startNodeInputParameters: [
      'quoteId',
      'comment',
      'requestorId',
      'signatureConfiguration'
    ],
    executionSummary: QuoteRequiresApprovalExecutionSummary,
    providesConditionalProperties: {
      TOTAL_CONTRACT_VALUE: {
        label: 'Total contract value',
        operators: {
          // Currency-only unit selector — percentage of self isn't meaningful.
          // Picking e.g. USD adds an implicit `$["sequence.quote.currency"] === "USD"`
          // check at runtime, sourced from the upstream currency output.
          LESS_THAN: {
            label: 'Less than',
            valueInputComponent: contractValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: lessThanAmount({
              currencySemanticType: semanticTypes.sequence.quote.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          },
          MORE_THAN: {
            label: 'More than',
            valueInputComponent: contractValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: moreThanAmount({
              currencySemanticType: semanticTypes.sequence.quote.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          },
          EQUALS: {
            label: 'Equals',
            valueInputComponent: contractValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: equalsAmount({
              currencySemanticType: semanticTypes.sequence.quote.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.totalContractValue
      },
      TOTAL_DISCOUNT_VALUE: {
        label: 'Total discount value',
        operators: {
          // Compare the discount as either a currency amount (any enabled
          // account currency, with a runtime check that the quote currency
          // matches) or as a percentage of the upstream total contract value.
          LESS_THAN: {
            label: 'Less than',
            valueInputComponent: discountValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: lessThanAmount({
              percentageBaseSemanticType:
                semanticTypes.sequence.quote.totalContractValue,
              currencySemanticType: semanticTypes.sequence.quote.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          },
          MORE_THAN: {
            label: 'More than',
            valueInputComponent: discountValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: moreThanAmount({
              percentageBaseSemanticType:
                semanticTypes.sequence.quote.totalContractValue,
              currencySemanticType: semanticTypes.sequence.quote.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          },
          EQUALS: {
            label: 'Equals',
            valueInputComponent: discountValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: equalsAmount({
              percentageBaseSemanticType:
                semanticTypes.sequence.quote.totalContractValue,
              currencySemanticType: semanticTypes.sequence.quote.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.totalDiscountValue
      },
      PRODUCTS: {
        label: 'Products',
        operators: {
          INCLUDES_ALL: {
            label: 'Includes all',
            valueInputComponent: ProductValueInput,
            functionString: arrayIncludesAll,
            formatPreviewValue: formatProductCount
          },
          EXCLUDES_ALL: {
            label: 'Excludes all',
            valueInputComponent: ProductValueInput,
            functionString: arrayExcludesAll,
            formatPreviewValue: formatProductCount
          },
          INCLUDES: {
            label: 'Includes',
            valueInputComponent: ProductValueInput,
            functionString: arrayIncludes,
            formatPreviewValue: formatProductCount
          },
          EXCLUDES: {
            label: 'Excludes',
            valueInputComponent: ProductValueInput,
            functionString: arrayExcludes,
            formatPreviewValue: formatProductCount
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.productIds
      },
      PRODUCT: productConditionalProperty,
      PHASE: phaseConditionalProperty,
      QUOTE_TEMPLATE: quoteTemplateConditionalProperty,
      PREVIOUS_APPROVAL: previousApprovalConditionalProperty,
      CUSTOMER: customerConditionalProperty({
        customerIdSemanticType: semanticTypes.sequence.customer.id,
        customPropertyKeys
      }),
      CURRENCY: {
        label: 'Currency',
        operators: {
          EQUALS: {
            label: 'Equals',
            valueInputComponent: CurrencyValueInput,
            functionString: equalsText,
            formatPreviewValue: formatCurrencyCode
          },
          NOT_EQUALS: {
            label: 'Does not equal',
            valueInputComponent: CurrencyValueInput,
            functionString: notEqualsText,
            formatPreviewValue: formatCurrencyCode
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.currency
      },
      DEAL_TYPE: {
        label: 'Deal type',
        operators: {
          EQUALS: {
            label: 'Equals',
            valueInputComponent: DealTypeValueInput,
            functionString: equalsText,
            formatPreviewValue: formatDealType
          },
          NOT_EQUALS: {
            label: 'Does not equal',
            valueInputComponent: DealTypeValueInput,
            functionString: notEqualsText,
            formatPreviewValue: formatDealType
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.dealType
      },
      // A row can be added per operator, so a workflow can ask for a range.
      // Two of them answer the whole property on their own though, and
      // `exclusiveOf` has to name both directions to keep them apart: a day
      // count can't be 30 and also more than 30, and "is not set" says all
      // there is to say about a quote with no terms.
      PAYMENT_TERMS: {
        label: 'Payment terms',
        operators: {
          EQUALS: {
            label: 'Equals',
            valueInputComponent: PaymentTermsValueInput,
            exclusiveOf: ['NOT_EQUALS', 'MORE_THAN', 'LESS_THAN', 'IS_NOT_SET'],
            functionString: paymentTermsEquals,
            formatPreviewValue: formatPaymentTerms
          },
          NOT_EQUALS: {
            label: 'Does not equal',
            valueInputComponent: PaymentTermsValueInput,
            exclusiveOf: ['EQUALS', 'IS_NOT_SET'],
            functionString: paymentTermsNotEquals,
            formatPreviewValue: formatPaymentTerms
          },
          MORE_THAN: {
            label: 'More than',
            valueInputComponent: PaymentTermsValueInput,
            exclusiveOf: ['EQUALS', 'IS_NOT_SET'],
            functionString: paymentTermsMoreThan,
            formatPreviewValue: formatPaymentTerms
          },
          LESS_THAN: {
            label: 'Less than',
            valueInputComponent: PaymentTermsValueInput,
            exclusiveOf: ['EQUALS', 'IS_NOT_SET'],
            functionString: paymentTermsLessThan,
            formatPreviewValue: formatPaymentTerms
          },
          IS_NOT_SET: {
            label: 'Is not set',
            valueInputComponent: PaymentTermsValueInput,
            requiresValue: false,
            exclusiveOf: ['EQUALS', 'NOT_EQUALS', 'MORE_THAN', 'LESS_THAN'],
            functionString: paymentTermsIsNotSet
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.paymentTerms
      },
      TOTAL_DURATION: {
        label: 'Total duration',
        operators: {
          // Anchor each duration comparison on the quote's start date when
          // defined; the runtime helper falls back to 2026-01-01 otherwise.
          SHORTER_THAN: {
            label: 'Shorter than',
            valueInputComponent: DurationValueInput,
            gridStyle: durationGridStyle,
            functionString: durationLessThan({
              startDateSemanticType: semanticTypes.sequence.quote.startDate
            }),
            formatPreviewValue: formatConditionDuration
          },
          LONGER_THAN: {
            label: 'Longer than',
            valueInputComponent: DurationValueInput,
            gridStyle: durationGridStyle,
            functionString: durationGreaterThan({
              startDateSemanticType: semanticTypes.sequence.quote.startDate
            }),
            formatPreviewValue: formatConditionDuration
          },
          EQUALS: {
            label: 'Equals',
            valueInputComponent: DurationValueInput,
            gridStyle: durationGridStyle,
            functionString: durationEquals({
              startDateSemanticType: semanticTypes.sequence.quote.startDate
            }),
            formatPreviewValue: formatConditionDuration
          },
          NOT_EQUALS: {
            label: 'Does not equal',
            valueInputComponent: DurationValueInput,
            gridStyle: durationGridStyle,
            functionString: durationNotEquals({
              startDateSemanticType: semanticTypes.sequence.quote.startDate
            }),
            formatPreviewValue: formatConditionDuration
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.totalDuration
      },
      RAW_TEXT: {
        label: 'Raw text',
        operators: {
          INCLUDES: {
            label: 'Includes',
            valueInputComponent: TextValueInput,
            functionString: textIncludes,
            formatPreviewValue: formatText
          },
          EXCLUDES: {
            label: 'Excludes',
            valueInputComponent: TextValueInput,
            functionString: textExcludes,
            formatPreviewValue: formatText
          }
        },
        valueSemanticType: semanticTypes.sequence.quote.rawText
      },
      REQUESTOR: {
        label: 'Requestor',
        operators: {
          INCLUDES: {
            label: 'Includes',
            valueInputComponent: UserValueInput,
            functionString: arrayIncludes,
            formatPreviewValue: formatUserCount
          },
          EXCLUDES: {
            label: 'Excludes',
            valueInputComponent: UserValueInput,
            functionString: arrayExcludes,
            formatPreviewValue: formatUserCount
          }
        },
        valueSemanticType: semanticTypes.sequence.requestorId
      },
      COMMENT: {
        label: 'Comment',
        operators: {
          IS_NOT_EMPTY: {
            label: 'Is not empty',
            valueInputComponent: TextValueInput,
            requiresValue: false,
            exclusiveOf: ['IS_EMPTY'],
            functionString: textIsNotEmpty
          },
          IS_EMPTY: {
            label: 'Is empty',
            valueInputComponent: TextValueInput,
            requiresValue: false,
            exclusiveOf: ['IS_NOT_EMPTY', 'INCLUDES', 'EXCLUDES'],
            functionString: textIsEmpty
          },
          INCLUDES: {
            label: 'Includes',
            valueInputComponent: TextValueInput,
            exclusiveOf: ['IS_EMPTY'],
            functionString: textIncludes,
            formatPreviewValue: formatText
          },
          EXCLUDES: {
            label: 'Excludes',
            valueInputComponent: TextValueInput,
            exclusiveOf: ['IS_EMPTY'],
            functionString: textExcludes,
            formatPreviewValue: formatText
          }
        },
        valueSemanticType: semanticTypes.sequence.quoteApproval.requestorComment
      }
    }
  }
}
